import React, { useState, useEffect } from 'react';
import api from '../api';
import { C, cs } from '../styles/common';

// Put the board and orders back to a point (Phase 6, slice 3; 6 Oct 2026).
//
// The director's words: ask before reverting, with exactly two answers for
// what came after the point -- keep it marked NEW, or remove it -- for runs
// and for orders alike; and "make sure the wording is simple, clear and
// comprehensive: don't make the director think what the app is asking or
// proposing, it must be immediately evident."
//
// 6 Oct, later: "Revert back to this point" opens a warning in his words --
// "Are you sure you want to change the Schedule and Orders back to this
// point?" -- Yes reverts, No goes back to viewing only. So the pop-up is a
// confirmation: what changes in a few lines (every change folded away below
// them), the two questions only when this point raises them, then Yes / No.
// Nothing is written until Yes, and the server refuses if anything changed
// while he read.

const TEAL = C.teal, AMBER = '#f59e0b', DIM = '#9ca3af', GREEN = '#4ade80';

const day = (s) => new Date(s + 'T12:00:00Z').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const range = (a, b) => (!b || a === b ? day(a) : `${day(a)} – ${day(b)}`);
const n = (k, one, many = one + 's') => `${k} ${k === 1 ? one : many}`;
const pcs = (q) => `${(q || 0).toLocaleString('en-GB')} pcs`;
const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
const key = (keepRuns, keepOrders) => `${keepRuns ? 'keep' : 'remove'}_runs_${keepOrders ? 'keep' : 'remove'}_orders`;

export default function PutBackDialog({ eventId, initial = null, onClose, onDone }) {
  const [data, setData] = useState(initial);
  const [error, setError] = useState(null);
  const [keepRuns, setKeepRuns] = useState(null);
  const [keepOrders, setKeepOrders] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState(null);
  const [details, setDetails] = useState(false);

  function load() {
    setData(null); setError(null); setRefused(null);
    api.get('/api/v1/history/put-back', { params: { event: eventId } })
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data || { error: e.message }));
  }
  useEffect(() => { if (!initial) load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const askRuns = data ? data.ask.runs : [];
  const askOrders = data ? data.ask.orders : [];
  const answered = (!askRuns.length || keepRuns !== null) && (!askOrders.length || keepOrders !== null);
  const unanswered = (askRuns.length && keepRuns === null ? 1 : 0) + (askOrders.length && keepOrders === null ? 1 : 0);
  // Until he answers, show what the board does either way with "keep".
  const plan = data ? data.plans[key(keepRuns !== false, keepOrders !== false)] : null;
  const mach = (id) => (data && data.machines[id]) || `machine ${id}`;

  async function confirm() {
    setBusy(true); setRefused(null);
    try {
      const { data: r } = await api.post('/api/v1/history/put-back', {
        event: eventId, expect: data.expect,
        keep_runs: askRuns.length ? keepRuns : undefined,
        keep_orders: askOrders.length ? keepOrders : undefined,
      });
      onDone(r);
    } catch (e) {
      setRefused(e.response?.data || { error: e.message });
      setBusy(false);
    }
  }

  // What changes, in a few lines.
  const summary = [];
  if (plan) {
    const back = plan.runs.filter(r => !r.new && r.kind !== 'off');
    const notExactly = (plan.shifted || []).filter(x => x.why === 'ran_there').length;
    if (back.length) {
      summary.push(`${n(back.length, 'run')} ${back.length === 1 ? 'goes' : 'go'} back to where ${back.length === 1 ? 'it was' : 'they were'}`
        + (notExactly ? ` (${notExactly === 1 ? 'one' : notExactly} not exactly: another run has run there since)` : ''));
    }
    if (plan.ran_since.length) {
      const one = plan.ran_since.length === 1;
      summary.push(`${n(plan.ran_since.length, 'run')} that ${one ? 'has' : 'have'} run since ${one ? 'stays' : 'stay'} where ${one ? 'it' : 'they'} ran`);
    }
    const ordersBack = plan.orders.filter(o => o.kind !== 'stays_deleted');
    if (ordersBack.length) {
      const one = ordersBack.length === 1;
      summary.push(`${n(ordersBack.length, 'order')} ${one ? 'goes' : 'go'} back to how ${one ? 'it was' : 'they were'}: ${ordersBack.map(o => o.number).join(', ')}`);
    }
    if (!summary.length) summary.push('Nothing already on the board or in the orders changes');
  }

  return (
    <div style={cs.overlay} onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="putback-title" style={{ ...cs.modal, maxWidth: 680, maxHeight: '90vh' }}>
        <div style={{ ...cs.modalHeader, alignItems: 'flex-start', gap: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ color: AMBER, fontSize: 20, marginTop: 2 }} />
            <div>
              <div id="putback-title" style={{ ...cs.modalTitle, lineHeight: 1.35 }}>
                {data && data.nothing
                  ? 'Nothing to revert'
                  : 'Are you sure you want to change the Schedule and Orders back to this point?'}
              </div>
              {data && (
                <div style={{ fontSize: 12.5, color: DIM, marginTop: 5, lineHeight: 1.5 }}>
                  {data.point.said}, just after <i>“{data.point.summary}”</i>
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} style={cs.closeBtn} aria-label="Close" disabled={busy}>×</button>
        </div>

        <div style={{ ...cs.modalBody, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {error && <Refusal r={error} onLookAgain={load} />}
          {!error && !data && <div style={{ color: C.muted, fontSize: 13 }}>Working out what would change…</div>}

          {data && data.nothing && (
            <div style={{ fontSize: 14, lineHeight: 1.55 }}>
              The board and the orders are exactly as they were at this point.
            </div>
          )}

          {data && !data.nothing && (
            <>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: C.muted, marginBottom: 7 }}>What changes</div>
                <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13.5, lineHeight: 1.5 }}>
                  {summary.map((t, i) => <li key={i}>{t}.</li>)}
                </ul>
                <button onClick={() => setDetails(v => !v)} aria-expanded={details}
                  style={{ background: 'none', border: 'none', color: TEAL, cursor: 'pointer', padding: 0, marginTop: 8, fontSize: 12.5 }}>
                  <i className={`fa-solid fa-chevron-${details ? 'down' : 'right'}`} style={{ fontSize: 10, marginRight: 6 }} />
                  {details ? 'Hide every change' : 'See every change'}
                </button>
              </div>

              {details && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, borderLeft: `2px solid ${C.border}`, paddingLeft: 12 }}>
                  <Section title="On the board">
                    <BoardList plan={plan} mach={mach} />
                  </Section>
                  {plan.orders.length > 0 && (
                    <Section title="Orders">
                      {plan.orders.map(o => (
                        <Item key={o.id} title={o.number} tone={o.kind === 'stays_deleted' ? DIM : AMBER}>
                          {o.said.map((t, i) => <div key={i}>{cap(t)}</div>)}
                        </Item>
                      ))}
                    </Section>
                  )}
                </div>
              )}

              {askRuns.length > 0 && (
                <Question
                  title={`${n(askRuns.length, 'run')} ${askRuns.length === 1 ? 'was' : 'were'} put on the board after this point`}
                  list={askRuns.map(r => (
                    <span key={r.key}><b>{r.part || 'A run'}</b> — {mach(r.machine_id)}, {range(r.from, r.to)}
                      {r.ran && <span style={{ color: GREEN }}> · has run, stays either way</span>}</span>
                  ))}
                  ask={`What should happen to ${askRuns.length === 1 ? 'it' : 'them'}?`}
                  value={keepRuns} onChange={setKeepRuns}
                  keep={{
                    label: `Keep ${askRuns.length === 1 ? 'it' : 'them'}, marked NEW`,
                    says: keptRunsSays(data, keepOrders, askRuns.length, mach),
                  }}
                  remove={{
                    label: `Remove ${askRuns.length === 1 ? 'it' : 'them'}`,
                    says: removedRunsSays(askRuns),
                  }}
                />
              )}

              {askOrders.length > 0 && (
                <Question
                  title={ordersTitle(askOrders)}
                  list={askOrders.map((o, i) => (
                    <span key={i}><b>{o.number}</b>{o.what === 'line' ? `: ${o.lines.join(', ') || 'a line'} added` : `${o.customer ? ` · ${o.customer}` : ''} — ${o.lines.join(', ') || 'no lines'}`}
                      {o.runs > 0 && <span style={{ color: DIM }}> · {n(o.runs, 'day')} on the board</span>}
                      {o.ran && <span style={{ color: GREEN }}> · has run, stays either way</span>}</span>
                  ))}
                  ask={`What should happen to ${askOrders.length === 1 ? 'it' : 'them'}?`}
                  value={keepOrders} onChange={setKeepOrders}
                  keep={{
                    label: `Keep ${askOrders.length === 1 ? 'it' : 'them'}, marked NEW`,
                    says: keptOrdersSays(askOrders, askRuns.length ? keepRuns : true),
                  }}
                  remove={{
                    label: `Remove ${askOrders.length === 1 ? 'it' : 'them'}, with ${askOrders.length === 1 ? 'its' : 'their'} runs`,
                    says: removedOrdersSays(askOrders),
                  }}
                />
              )}

              <div style={{ fontSize: 12.5, color: DIM }}>
                <i className="fa-solid fa-rotate-left" style={{ marginRight: 6, fontSize: 11 }} />
                Changed your mind afterwards? One <b style={{ color: '#e5e7eb' }}>Undo</b> on the board puts it all back the way it is now.
              </div>

              {refused && <Refusal r={refused} onLookAgain={() => { setKeepRuns(null); setKeepOrders(null); load(); }} />}
            </>
          )}
        </div>

        <div style={{ ...cs.modalFooter, alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={cs.btnSecondary} disabled={busy}>
            {data && data.nothing ? 'Back to viewing' : 'No, back to viewing only'}
          </button>
          {data && !data.nothing && (
            <button onClick={confirm} disabled={!answered || busy}
              style={{ ...cs.btnPrimary, background: AMBER, color: '#1a1205', opacity: !answered || busy ? 0.5 : 1, cursor: !answered || busy ? 'not-allowed' : 'pointer' }}>
              {busy ? 'Reverting…'
                : !answered ? (unanswered > 1 ? 'Answer both questions above' : 'Answer the question above')
                : 'Yes, revert'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── what goes back ──────────────────────────────────────────────────────────

function BoardList({ plan, mach }) {
  const shiftedBy = new Map();
  for (const s of plan.shifted || []) shiftedBy.set(String(s.key).split(':').slice(1).join(':'), s);
  const runs = plan.runs.filter(r => !r.new && r.kind !== 'off');
  if (!runs.length && !plan.ran_since.length) {
    return <div style={{ fontSize: 13, color: DIM }}>Nothing on the board moves.</div>;
  }
  return (
    <>
      {runs.map(r => {
        const s = shiftedBy.get(r.key);
        const lines = [];
        if (r.kind === 'returns') {
          lines.push(`Comes back on ${mach(r.after.machine_id)}: ${range(r.after.from, r.after.to)} (${n(r.after.days, 'day')})`);
        } else if (r.kind === 'quantity') {
          lines.push(`Quantity goes back: ${pcs(r.now.qty)} → ${pcs(r.after.qty)}`);
        } else if (r.whole) {
          lines.push(r.now.machine_id === r.after.machine_id
            ? `${mach(r.now.machine_id)}: ${range(r.now.from, r.now.to)} → ${range(r.after.from, r.after.to)}`
            : `${mach(r.now.machine_id)} ${range(r.now.from, r.now.to)} → ${mach(r.after.machine_id)} ${range(r.after.from, r.after.to)}`);
        } else {
          const d = r.days;
          if (d.moved.length) {
            lines.push(`${n(d.moved.length, 'day')} ${d.moved.length === 1 ? 'goes' : 'go'} back: `
              + d.moved.slice(0, 4).map(m => `${day(m.from)} → ${day(m.to)}${m.machine_to !== m.machine_from ? ` on ${mach(m.machine_to)}` : ''}`).join(', ')
              + (d.moved.length > 4 ? ', …' : ''));
          }
          if (d.back.length) lines.push(`${n(d.back.length, 'day')} ${d.back.length === 1 ? 'comes' : 'come'} back: ${range(d.back[0], d.back[d.back.length - 1])}`);
          if (d.off.length) lines.push(`${n(d.off.length, 'day')} ${d.off.length === 1 ? 'comes' : 'come'} off: ${range(d.off[0], d.off[d.off.length - 1])}`);
          if (!lines.length) lines.push(`${mach((r.after || r.now).machine_id)}: ${range((r.after || r.now).from, (r.after || r.now).to)}`);
        }
        const where = r.kind === 'returns' || r.whole ? null : mach((r.after || r.now).machine_id);
        return (
          <Item key={r.key} title={r.part || 'A run'} meta={where} tone={r.kind === 'returns' ? GREEN : AMBER}>
            {lines.map((l, i) => <div key={i}>{l}</div>)}
            {s && s.why === 'ran_there' && (
              <div style={{ color: AMBER }}>
                Not exactly where it was: {(s.jumped || []).map(j => j.part).filter(Boolean).join(', ') || 'another run'} ran there since, so it takes the first free {s.to[0] === s.to[1] ? 'day' : 'days'} after.
              </div>
            )}
          </Item>
        );
      })}
      {plan.ran_since.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 12, color: DIM, margin: '4px 0 6px' }}>These have run since, so they stay where they ran:</div>
          {plan.ran_since.map(r => (
            <Item key={r.key} title={r.part || 'A run'} tone={GREEN}>
              {mach(r.machine_id)}: {range(r.from, r.to)}
            </Item>
          ))}
        </div>
      )}
    </>
  );
}

// ── what each answer does ───────────────────────────────────────────────────

function keptRunsSays(data, keepOrders, count, mach) {
  const p = data.plans[key(true, keepOrders !== false)];
  const pushed = p.runs.filter(r => r.new && r.pushed && r.now && r.after);
  let says = `${count === 1 ? 'It stays' : 'They stay'} on the board with a NEW label, until you dismiss it.`;
  if (pushed.length) {
    says += ` ${pushed.length === 1 ? 'One sits' : `${pushed.length} sit`} where an older run goes back, so ${pushed.length === 1 ? 'it moves' : 'they move'} later, only as far as needed: `
      + pushed.map(r => `${r.part || 'a run'} on ${mach(r.now.machine_id)}, ${range(r.now.from, r.now.to)} → ${range(r.after.from, r.after.to)}`).join('; ') + '.';
  }
  return says;
}

function removedRunsSays(askRuns) {
  const ran = askRuns.filter(r => r.ran).length;
  let says = 'The board is exactly as it was then.';
  if (ran) says += ` ${ran === askRuns.length ? (ran === 1 ? 'It has' : 'They have') : (ran === 1 ? 'The one that has' : `The ${ran} that have`)} already run ${ran === 1 ? 'stays' : 'stay'} — that happened.`;
  return says;
}

function ordersTitle(askOrders) {
  const orders = askOrders.filter(o => o.what === 'order').length;
  const lines = askOrders.length - orders;
  const a = orders ? `${n(orders, 'order')} ${orders === 1 ? 'was' : 'were'} taken` : null;
  const b = lines ? `${n(lines, 'line')} ${lines === 1 ? 'was' : 'were'} added to an order` : null;
  return a && b ? `${a}, and ${b}, after this point` : `${a || b} after this point`;
}

function keptOrdersSays(askOrders, keepRuns) {
  const one = askOrders.length === 1;
  let says = `${one ? 'It stays' : 'They stay'} on the Orders page with a NEW label, until you dismiss it.`;
  if (keepRuns === false && askOrders.some(o => o.runs > 0 && !o.ran)) {
    says += ` ${one ? 'Its' : 'Their'} runs come off the board with the other new runs, so ${one ? 'it waits' : 'they wait'} to be scheduled.`;
  }
  return says;
}

function removedOrdersSays(askOrders) {
  const ran = askOrders.filter(o => o.ran);
  const runs = askOrders.filter(o => !o.ran).reduce((k, o) => k + (o.runs || 0), 0);
  let says = runs ? `Deleted, together with ${n(runs, 'day')} on the board.` : 'Deleted.';
  if (ran.length) says += ` ${ran.map(o => o.number).join(', ')} ${ran.length === 1 ? 'stays' : 'stay'}, marked NEW: ${ran.length === 1 ? 'its' : 'their'} production has started.`;
  return says;
}

// ── pieces ──────────────────────────────────────────────────────────────────

function Section({ title, children }) {
  return (
    <div>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: C.muted, marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{children}</div>
    </div>
  );
}

function Item({ title, meta, tone, children }) {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: 13, lineHeight: 1.5 }}>
      <span style={{ width: 3, borderRadius: 2, background: tone, flexShrink: 0 }} />
      <div style={{ minWidth: 0 }}>
        <b>{title}</b>{meta && <span style={{ color: DIM }}> · {meta}</span>}
        <div style={{ color: '#d1d5db' }}>{children}</div>
      </div>
    </div>
  );
}

function Question({ title, list, ask, value, onChange, keep, remove }) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, background: C.surfaceAlt }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12.5, color: '#d1d5db', marginBottom: 10, lineHeight: 1.5 }}>
        {list}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{ask}</div>
      <div role="radiogroup" aria-label={ask} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 8 }}>
        {[[true, keep], [false, remove]].map(([v, o]) => {
          const on = value === v;
          return (
            <button key={String(v)} role="radio" aria-checked={on} onClick={() => onChange(v)}
              style={{ textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: 'system-ui, sans-serif',
                       background: on ? C.tealDim : C.surface, border: `1.5px solid ${on ? TEAL : C.border}`, color: C.text }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 700, color: on ? TEAL : C.text }}>
                <span style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${on ? TEAL : C.muted}`, flexShrink: 0,
                               boxShadow: on ? `inset 0 0 0 3px ${C.surface}` : 'none', background: on ? TEAL : 'transparent' }} />
                {o.label}
              </div>
              <div style={{ fontSize: 12.5, color: DIM, marginTop: 5, lineHeight: 1.5 }}>{o.says}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Refusal({ r, onLookAgain }) {
  return (
    <div style={{ ...cs.errorBanner, marginBottom: 0 }}>
      <div style={{ fontWeight: 700 }}>{r.error}</div>
      {r.why && <div style={{ marginTop: 4 }}>{r.why}</div>}
      {(r.options || []).some(o => o.do === 'look_again') && (
        <button onClick={onLookAgain} style={{ ...cs.btnSecondary, marginTop: 8, padding: '6px 12px' }}>Look again</button>
      )}
    </div>
  );
}
